/**
 * Where the picture is: the art window, measured from the card's own pixels.
 *
 * Pass 3 cut the art window at a constant - 8% in from each side, 10.5% down, 44%
 * tall - because a sweep against the stored fingerprints said those numbers hash
 * 4-12 bits from the right artwork. That constant is a property of *a* standard
 * card, and pass 4 then measured what it costs: the classifier's own art bottom
 * came out at 0.86-0.89 of the card where the picture ends near 0.54, because the
 * type box's rules text covers enough of each row to pass a busy-row test. A device
 * that cuts the window from the classifier's measurement cuts the text.
 *
 * This module is the measurement rebuilt against real cards, for real cards. Two
 * facts from that measurement drive the design:
 *
 *  - **The printed art window is nearly the same on every standard frame.** Across
 *    eras the picture starts at 11-12% of the card and ends at 52-55%, whatever the
 *    frame looks like around it. So the window is not an era question at all, and a
 *    detector that answers it does not need to know the era first - which removes
 *    the circularity of the old code, where the era decided where to look and the
 *    crop decided the era.
 *  - **Text is not a smaller amount of picture, it is a different signal.** The
 *    rules box is strokes on flat paper: *locally* high variance in thin lines, and
 *    bright. A painting is low-frequency everywhere. Measuring the mean 5x5
 *    standard deviation per row separates them (7-27 inside a picture against 29-68
 *    in the type box on the cards measured), where the fraction of "busy" pixels -
 *    what pass 3 used - does not (both are 0.5-1.0).
 *
 * The cut is therefore chosen by a small linear score over row-region features,
 * with the weights *calibrated* against real cards rather than typed in:
 * `calibrateArtWindowWeights` takes (profile, true cut) pairs and returns the
 * nearest-centroid direction that separates the true cut from its neighbours. The
 * command that does the calibration (`npm run catalog:recognise -- --calibrate`)
 * also prints what the direction separated, and the benchmark measures what the
 * resulting detector does on cards it never calibrated on - both numbers are in the
 * ADR, and the second one is the one that decides what ships.
 *
 * What is deliberately *not* here: any treatment *classification*. Whether a picture is
 * standard, full-art or borderless is a question about how far down the card the picture
 * continues, which is a different measurement on the same profile and lives in
 * `frame.ts`. What this module does report is the raw measurement that question rests on
 * - `extended` and, through the evidence, the deepest row that is still a picture.
 */
import { assertGreyImage } from './geometry.js';
/** A pixel is structured when its neighbour differs by this much. */
export const GRADIENT = 6;
/** Local 5x5 standard deviation sampled this often along a row. */
const STD_STEP = 2;
/** A row carries printed text when its local standard deviation exceeds this. */
export const TEXT_STD = 30;
/** Paper: what the printed card's rules box is, in levels. */
export const PAPER_LEVEL = 225;
/** Row statistics for a horizontal span of the frame. */
export function rowStats(frame, y, from = 0.02, to = 0.98) {
    const x0 = Math.max(0, Math.round(frame.width * from));
    const x1 = Math.min(frame.width, Math.round(frame.width * to));
    const span = Math.max(1, x1 - x0);
    let structured = 0;
    let gradient = 0;
    let paper = 0;
    let sum = 0;
    let stdSum = 0;
    let stdCount = 0;
    const radius = 2;
    for (let x = x0; x < x1; x += 1) {
        const level = frame.levels[y * frame.width + x] ?? 0;
        const right = frame.levels[y * frame.width + Math.min(frame.width - 1, x + 1)] ?? 0;
        const difference = Math.abs(level - right);
        gradient += difference;
        if (difference > GRADIENT)
            structured += 1;
        if (level >= PAPER_LEVEL)
            paper += 1;
        sum += level;
        if (x % STD_STEP === 0 && x >= radius && y >= radius && x < frame.width - radius && y < frame.height - radius) {
            let total = 0;
            let square = 0;
            let count = 0;
            for (let dy = -radius; dy <= radius; dy += 1) {
                for (let dx = -radius; dx <= radius; dx += 1) {
                    const value = frame.levels[(y + dy) * frame.width + (x + dx)] ?? 0;
                    total += value;
                    square += value * value;
                    count += 1;
                }
            }
            const mean = total / count;
            stdSum += Math.sqrt(Math.max(0, square / count - mean * mean));
            stdCount += 1;
        }
    }
    return {
        busy: structured / span,
        std: stdSum / Math.max(1, stdCount),
        gradient: gradient / span,
        paper: paper / span,
        mean: sum / span,
    };
}
/**
 * The frame's rows, profiled across the card's middle.
 *
 * The outer 8% of each row is excluded on purpose: that is where the printed margin
 * and its bevel are, and a bevel line is high-frequency structure that would
 * otherwise read as text on every row of every card.
 */
export function profileFrame(frame, from = 0.08, to = 0.92) {
    assertGreyImage(frame, 'the frame to profile');
    const rows = [];
    for (let y = 0; y < frame.height; y += 1)
        rows.push(rowStats(frame, y, from, to));
    return { rows, from, to };
}
/**
 * The features a candidate art-bottom cut is judged on.
 *
 * Region A is the 12% of the card above the cut, B the 12% below it. In card
 * fractions, so a capture's pixel size never enters the score.
 */
export const BOTTOM_FEATURES = [
    'pictureSmoothness', // mean 3x3 std above, /64: a picture is low-frequency
    'textAmountBelow', // mean 3x3 std below, /64: the type box is strokes
    'textRowShareBelow', // rows below whose std passes TEXT_STD
    'textRowShareAbove', // the same above: a penalty, the picture is not text
    'paperBelow', // the rules box is printed paper
    'paperAbove', // a penalty: the picture is not paper
    'busyJump', // mean busy below minus above: the type line is a busy bar
    'lumaBelow', // mean level below, /255
    // Both means, not just the one below: the printed type line is a *step* in luma,
    // and a linear score can only form the step if it can see both sides of it. On the
    // real cards measured it is the largest single signal at the picture's bottom edge
    // (46 levels on Invisible Stalker, 55 on Baneslayer Angel, 86 on Eventide's Shadow).
    'lumaAbove',
];
/** The same for an art-top cut, where the card is frame above and picture below. */
export const TOP_FEATURES = [
    'pictureSmoothness', // mean 3x3 std below, /64
    'textAmountAbove', // mean 3x3 std above, /64: the title bar prints a name
    'textRowShareAbove', // rows above whose std passes TEXT_STD
    'textRowShareBelow', // a penalty: the picture is not text
    'paperAbove', // the title bar is a printed panel
    'paperBelow', // a penalty
    'busyJump', // mean busy below minus above
    'lumaBelow', // mean level below, /255
    'lumaAbove', // and above, so the score can see the step into the title bar
];
const mean = (values) => values.length === 0 ? 0 : values.reduce((total, value) => total + value, 0) / values.length;
function region(profile, from, to) {
    const rows = profile.rows;
    const start = Math.max(0, Math.round(from * rows.length));
    const end = Math.min(rows.length, Math.round(to * rows.length));
    if (end <= start)
        return [];
    return rows.slice(start, end);
}
const shareOf = (rows, predicate) => rows.length === 0 ? 0 : rows.filter(predicate).length / rows.length;
/** The window the features are computed over, in card fractions. */
export const FEATURE_SPAN = 0.12;
export function bottomFeatures(profile, cut) {
    const above = region(profile, cut - FEATURE_SPAN, cut);
    const below = region(profile, cut, cut + FEATURE_SPAN);
    return [
        mean(above.map((row) => row.std)) / 64,
        mean(below.map((row) => row.std)) / 64,
        shareOf(below, (row) => row.std > TEXT_STD),
        shareOf(above, (row) => row.std > TEXT_STD),
        mean(below.map((row) => row.paper)),
        mean(above.map((row) => row.paper)),
        mean(below.map((row) => row.busy)) - mean(above.map((row) => row.busy)),
        mean(below.map((row) => row.mean)) / 255,
        mean(above.map((row) => row.mean)) / 255,
    ];
}
export function topFeatures(profile, cut) {
    const above = region(profile, cut - FEATURE_SPAN, cut);
    const below = region(profile, cut, cut + FEATURE_SPAN);
    return [
        mean(below.map((row) => row.std)) / 64,
        mean(above.map((row) => row.std)) / 64,
        shareOf(above, (row) => row.std > TEXT_STD),
        shareOf(below, (row) => row.std > TEXT_STD),
        mean(above.map((row) => row.paper)),
        mean(below.map((row) => row.paper)),
        mean(below.map((row) => row.busy)) - mean(above.map((row) => row.busy)),
        mean(below.map((row) => row.mean)) / 255,
        mean(above.map((row) => row.mean)) / 255,
    ];
}
export const dot = (weights, features) => {
    if (weights.length !== features.length) {
        throw new Error(`weights have ${weights.length} components but the features have ${features.length}`);
    }
    return weights.reduce((total, weight, index) => total + weight * features[index], 0);
};
/**
 * Learn the cut's weights from labelled samples, by nearest centroid.
 *
 * Deliberately the simplest learner that can work: the mean feature vector of the
 * true cuts minus the mean of the neighbouring cuts. It is reproducible from a
 * seed, it has no hyper-parameters to tune, and a failure to separate is visible in
 * the returned counts instead of hidden behind an optimiser's convergence. The
 * caller chooses what counts as a neighbour, which is where the honesty lives: the
 * negatives are the cuts the detector would otherwise make.
 */
export function calibrateWindowWeights(samples) {
    const positives = samples.filter((sample) => sample.positive);
    const negatives = samples.filter((sample) => !sample.positive);
    if (positives.length === 0 || negatives.length === 0) {
        throw new Error('calibration needs at least one positive and one negative sample');
    }
    const size = positives[0].features.length;
    for (const sample of samples) {
        if (sample.features.length !== size) {
            throw new Error(`sample features are ${sample.features.length} long, expected ${size}`);
        }
    }
    const weights = [];
    for (let index = 0; index < size; index += 1) {
        const positiveMean = mean(positives.map((sample) => sample.features[index]));
        const negativeMean = mean(negatives.map((sample) => sample.features[index]));
        weights.push(positiveMean - negativeMean);
    }
    const scale = weights.reduce((total, weight) => total + Math.abs(weight), 0) || 1;
    const normalized = weights.map((weight) => weight / scale);
    const positiveScore = mean(positives.map((sample) => dot(normalized, sample.features)));
    const negativeScore = mean(negatives.map((sample) => dot(normalized, sample.features)));
    const threshold = (positiveScore + negativeScore) / 2;
    const separated = samples.filter((sample) => (dot(normalized, sample.features) >= threshold) === sample.positive).length /
        samples.length;
    return { weights: normalized, positives: positives.length, negatives: negatives.length, separated };
}
/**
 * The weights the detector ships with, calibrated on real Scryfall cards.
 *
 * (They ship to the *classifier*: the fingerprint is still cut from the swept constant,
 * because the benchmark says that cut beats this measurement - see the note below the
 * insets, and ADR 0008.)
 *
 * Produced by `npm run catalog:recognise -- --calibrate --per-era=12 --seed=7`, which
 * downloads the cards, locates each one's printed art crop and runs
 * `calibrateWindowWeights` over the labels: 48 cards sampled, 37 whose crop locked
 * (correlation >= 0.9) and taught something. They are committed rather than
 * recomputed at runtime because a device cannot download a calibration set, and
 * because a number that changes under a reader's feet is not a measurement. What the
 * direction separated: 70.7% of the bottom labels and 65.6% of the top labels - barely
 * better than a coin toss per *label*, which is the honest state of a two-class nearest
 * centroid over 37 cards. What it is used for is the argmax of that direction along a
 * profile, which is a much easier question than ranking one cut on its own, and the
 * command's held-out sample is where that shows up as a number.
 *
 * **The shape this window has to have.** These weights locate a rectangle whose shape
 * is the printed crop's - 0.84 of the card's width and 1.37 of that as its height (44%
 * of the card) - because the fingerprints in the index are hashes of those crops
 * squashed to 64x64, and a window with a *different* shape squashes differently on its
 * way to the same grid. The two cuts below are measured independently, so the shape is
 * whatever the two measurements say it is, and the pass that built this measured what
 * that costs: a truth locator whose window was 8% short made every window the detector
 * produced read as *worse* than the printed constant at matching the artwork, while
 * being more accurate at placing it. The shape is the thing to check first.
 */
export const ART_BOTTOM_WEIGHTS = [
    -0.1505, 0.0744, 0.1297, -0.2101, 0.0454, -0.0611, -0.1687, 0.0753, -0.0848,
];
export const ART_TOP_WEIGHTS = [
    -0.1674, 0.1697, 0.2535, -0.245, 0.0399, -0.0356, 0.0172, -0.0252, 0.0466,
];
/** The median value of a column profile over an inner span of it. */
export function medianStd(profile, from, to) {
    const start = Math.max(0, Math.round(from * profile.std.length));
    const end = Math.min(profile.std.length, Math.round(to * profile.std.length));
    if (end <= start)
        return 0;
    const values = profile.std.slice(start, end).sort((a, b) => a - b);
    const middle = Math.floor(values.length / 2);
    return values.length % 2 === 1
        ? values[middle]
        : ((values[middle - 1] + values[middle]) / 2);
}
function argmax(profile, from, to, weights, features) {
    const rows = profile.rows.length;
    const start = Math.max(0, Math.round(from * rows));
    const end = Math.min(rows, Math.round(to * rows));
    let bestCut = start;
    let bestScore = -Infinity;
    let runnerUp = -Infinity;
    for (let y = start; y <= end; y += 1) {
        const cut = (y + 0.5) / rows;
        const score = dot(weights, features(profile, cut));
        if (score > bestScore) {
            runnerUp = bestScore;
            bestScore = score;
            bestCut = cut;
        }
        else if (score > runnerUp) {
            runnerUp = score;
        }
    }
    return { cut: bestCut, score: bestScore, best: bestScore, runnerUp };
}
/**
 * Fraction of rows in a span that look like a picture: neither text nor paper.
 *
 * Deliberately strict about paper. A printed rules box is paper with strokes on it,
 * and at the resolution a device hands over the strokes are a minority of a row's
 * samples - so `std` alone calls a rules box a picture, which is how pass 4's
 * classifier came to report the picture running to 0.86 of the card. The paper test
 * is the one that survives downsampling: the box is still near-white.
 */
export function pictureRowShare(profile, from, to, paperLimit = 0.25) {
    const rows = region(profile, from, to);
    return shareOf(rows, (row) => row.std <= TEXT_STD && row.paper < paperLimit);
}
/**
 * The deepest row below a cut that still looks like the printed picture.
 *
 * This is where a full-art or a borderless picture *stops*, and it is measured from the
 * rows rather than from the score, because on those treatments the score is flat
 * (everything below the standard cut is picture) and its argmax is noise: with the
 * weights that ship, the deepest cut the score preferred on a borderless frame sat at
 * 0.674 of the card where the picture runs to the edge. The walk tolerates a thin line
 * *inside* the picture - a signature, or a borderless printing's copyright line - by
 * only stopping once three percent of the card has failed the picture test.
 */
export function pictureBottom(profile, from, to) {
    const rows = profile.rows.length;
    const start = Math.max(0, Math.round(from * rows));
    const end = Math.min(rows, Math.round(to * rows));
    const tolerance = Math.max(1, Math.round(rows * 0.03));
    let last = null;
    for (let y = start; y < end; y += 1) {
        const row = profile.rows[y];
        if (row.std <= TEXT_STD && row.paper < 0.25)
            last = y;
        else if (last !== null && y - last > tolerance)
            break;
    }
    return last === null ? null : (last + 0.5) / rows;
}
/**
 * The mean local standard deviation of each column between two rows.
 *
 * This is what finds the picture's left and right edges: the printed margin beside a
 * picture is flat frame colour, and the picture is not. The transition is a step in
 * this signal, so the edge is the crossing of a threshold rather than a maximum -
 * which is why it survives a frame whose colour varies (a gold border, a black one).
 */
export function columnProfile(frame, yFrom, yTo, from = 0, to = 1) {
    const startColumn = Math.max(0, Math.round(from * frame.width));
    const endColumn = Math.min(frame.width, Math.round(to * frame.width));
    const y0 = Math.max(2, Math.round(yFrom * frame.height));
    const y1 = Math.min(frame.height - 2, Math.round(yTo * frame.height));
    const std = [];
    // A 3x3 window rather than 5x5. Both work on the picture, but the window's size is
    // a blur: the profile starts to rise *before* the picture's edge by the window's
    // radius, and a 5x5 window put the measured edge 0.6% of the card outside the real
    // one on its own. The median in `pictureEdge` is what keeps a small window from
    // being noisy, so the blur can be small too.
    const radius = 1;
    for (let x = startColumn; x < endColumn; x += 1) {
        let total = 0;
        let count = 0;
        for (let y = y0; y < y1; y += 2) {
            if (x < radius || x >= frame.width - radius)
                continue;
            let sum = 0;
            let square = 0;
            let seen = 0;
            for (let dy = -radius; dy <= radius; dy += 1) {
                for (let dx = -radius; dx <= radius; dx += 1) {
                    const value = frame.levels[(y + dy) * frame.width + (x + dx)] ?? 0;
                    sum += value;
                    square += value * value;
                    seen += 1;
                }
            }
            const meanLevel = sum / seen;
            total += Math.sqrt(Math.max(0, square / seen - meanLevel * meanLevel));
            count += 1;
        }
        std.push(total / Math.max(1, count));
    }
    return { std, x0: startColumn, x1: endColumn, width: frame.width };
}
/**
 * The first column from an edge whose neighbourhood is as structured as a picture.
 *
 * Sub-pixel by interpolating where the smoothed profile crosses the threshold, which
 * matters: the two frame families this has to separate put their picture 8.2% and
 * 11% of the card in from the edge, and a whole-pixel crossing on a 320px card is
 * already 0.3% of the width.
 */
export function pictureEdge(profile, direction, options = {}) {
    const count = profile.std.length;
    if (count < 8)
        return null;
    // The threshold is relative to the picture itself, because how structured a
    // painting is varies enormously between a flat sky and a busy battlefield. What
    // does not vary is the ratio between the picture and the flat margin beside it.
    const interior = medianStd(profile, 0.3, 0.7);
    const threshold = options.threshold ?? Math.max(2.5, interior * 0.35);
    // The search starts *inside* the card's own printed border. Every card has a step
    // at its edge - the border meeting whatever is behind the card - and on a rectified
    // card that step is at 0 by construction. It is not the picture's edge, and taking
    // it for one puts the window 7% of the card out. 5% clears the widest printed
    // border measured (4%), leaves the narrowest picture margin (8.2%) in front of it,
    // and is itself wider than the blur of a 5x5 window over the border's step.
    const searchFrom = Math.max(0, Math.round((options.searchFrom ?? 0.05) * count));
    const searchTo = Math.min(count - 1, Math.round((options.searchTo ?? 0.25) * count));
    // The run that has to be a picture: a fifth of the card's width. The card's own
    // printed border is a step in this signal too, and it is followed by frame colour
    // rather than by a picture, which is the whole difference between the two.
    const medianOfRange = (from, to) => {
        const start = Math.max(0, Math.min(count - 1, from));
        const end = Math.max(start + 1, Math.min(count, to));
        const values = profile.std.slice(start, end).sort((a, b) => a - b);
        const middle = Math.floor(values.length / 2);
        return values.length === 0
            ? 0
            : values.length % 2 === 1
                ? values[middle]
                : ((values[middle - 1] + values[middle]) / 2);
    };
    // The panel's printed edge is one or two columns wide, so the *maximum* of a small
    // neighbourhood is what finds it. A median would suppress exactly the feature the
    // search is for - it did, and the sides then fell back to the printed default on
    // every real card in the sample.
    const smooth = (index) => Math.max(profile.std[Math.max(0, index - 1)] ?? 0, profile.std[index] ?? 0, profile.std[Math.min(count - 1, index + 1)] ?? 0);
    // The margin's own structure, measured from the card's edge to the candidate. On a
    // *printed* margin - a scan of a real card - this is not zero: film grain and the
    // scan give a flat frame colour a couple of levels of variance, and the quiet parts
    // of a picture a couple more. What separates them is not a threshold but the step
    // at the panel's edge, which is a printed line and stands well clear of both.
    const contrast = Math.max(2.5, interior * 0.5);
    // The walk always starts at the card's edge and moves inwards, so "the first
    // structured column" means the first one in from *that* side. Reversing a
    // low-index range would search the left side twice, which is a bug that costs the
    // picture's whole right edge.
    const indices = [];
    if (direction === 'left') {
        for (let index = searchFrom; index <= searchTo; index += 1)
            indices.push(index);
    }
    else {
        for (let index = count - 1 - searchFrom; index >= count - 1 - searchTo; index -= 1)
            indices.push(index);
    }
    const ordered = indices;
    const inward = direction === 'left' ? 1 : -1;
    let previous = ordered[0];
    for (const index of ordered) {
        const value = smooth(index);
        // A crossing only counts when the picture *continues*: five percent of the card
        // inwards has to be as structured as the picture, and everything between the
        // card's edge and here has to have been flat margin. The card's own printed
        // border is a step in this signal too, and it fails both halves of that test.
        const marginColumns = inward === 1 ? index - searchFrom : count - 1 - searchFrom - index;
        if (marginColumns < 4) {
            previous = index;
            continue;
        }
        const marginLevel = inward === 1 ? medianOfRange(searchFrom, index) : medianOfRange(index, count - 1 - searchFrom);
        const outward = inward === 1 ? medianOfRange(searchFrom, index) : medianOfRange(index, count - 1 - searchFrom);
        // Three tests, and a painting passes only the first of them at its own internal
        // edges. A strong vertical line *inside* a picture - a cliff, a pillar, the edge of
        // a table - is a step in this profile just like the panel's printed bevel is; what
        // distinguishes them is everything outside the candidate: outside the real edge is
        // flat frame colour, and outside a line inside the painting is more painting.
        const quiet = interior * 0.55;
        if (process.env.CHAOS_DEBUG_WINDOW && index < searchFrom + 6) {
            console.log(`  edge ${direction} i=${index} value=${value.toFixed(1)} margin=${marginLevel.toFixed(1)} outward=${outward.toFixed(1)} ` +
                `threshold=${threshold.toFixed(1)} contrast=${contrast.toFixed(1)} quiet=${quiet.toFixed(1)} interior=${interior.toFixed(1)}`);
        }
        if (value >= marginLevel + contrast && value >= threshold && outward < quiet) {
            // Interpolate between this sample and the one before it, which was below.
            // The fraction is clamped: on a real capture the profile is not monotone, and
            // an unclamped extrapolation put the picture's left edge at -0.2 of the card.
            const before = smooth(previous);
            const span = value - before;
            const target = Math.max(marginLevel + contrast, threshold);
            const fraction = span <= 0 ? 0 : Math.min(1, Math.max(0, (target - before) / span));
            const position = previous + (index - previous) * fraction;
            return (profile.x0 + position + 0.5) / profile.width;
        }
        previous = index;
    }
    return null;
}
/**
 * The printed art panel's inset from the card's side edges.
 *
 * Measured by pass 3's sweep against the stored fingerprints (8%), and confirmed in
 * pass 5 by locating the real art crop: over the standard-framed cards sampled, the
 * crop's left edge falls at 0.063-0.118 of the width, centred on 0.077. It is used
 * when the side edges cannot be measured, which on real cards is the usual case.
 */
export const PANEL_INSET = 0.08;
/**
 * The inset a borderless printing keeps: the printed bleed, not a frame.
 *
 * A picture that reaches the card's edges still stops a print's width short of the
 * cut line, and 2% is that width at the sizes the index's crops come from.
 */
export const BLEED_INSET = 0.02;
/*
 * What this window is *not*: the fingerprint.
 *
 * The rectangle the picture occupies and the rectangle a fingerprint is cut from are
 * the same only by accident, and the pass that built this measured the difference. The
 * index holds hashes of Scryfall's crops - 626x457, squashed to 64x64 - so a probe has
 * to have the crop's *shape* and sit where the crop sits. This detector returns the
 * picture's own rectangle: on a standard frame that is about 41% of the card's height
 * against the crop's 44%, and on a card whose picture fills the face it is the whole
 * face. Both are the right measurement and the wrong fingerprint, which is why
 * `recognise` cuts its probe from the swept constant window (8% in, 44% tall - the crop's
 * shape) and *reports* what this window would do as a probe: 60 of 160 cards matched
 * their stored artwork through it, against 86 through the constant, on the same frames.
 * A better-located window that is a worse cut still costs the artwork route, and the
 * tail - 10% of cards landing 0.4 of the card out - is the thing that would have to
 * close before this replaces the constant in the hash.
 */
/**
 * How far the score's best cut sits below the printed picture's bottom edge.
 *
 * A measured correction, not a taste: the printed type line - the thing the score is
 * looking for - sits just *below* where the picture stops, so the raw cut lands past it,
 * and the 160-card benchmark measures what the correction leaves behind: with 0.010
 * applied the band era's residual bias is -0.004 of the card (the cut sits 0.4% of the card
 * above the crop's bottom edge, over 33 cards). Applying a constant is
 * honest as long as it is measured, is stated, and is re-measured on cards the
 * calibration never saw; the command does all three, and the number here is the one that
 * leaves that residual smallest.
 */
export const ART_BOTTOM_BIAS = 0.01;
/**
 * How much better a deeper cut has to score before the picture is called full-art.
 *
 * The score is an L1-normalised linear combination, so its scale is the scale of the
 * features: 0.05 is about a fifth of the separation the calibration achieves between
 * a true cut and its neighbours, which is the smallest margin worth acting on.
 */
export const FULL_ART_SCORE_MARGIN = 0.05;
/**
 * The art window: where the picture is, and whether it runs past the standard bottom.
 *
 * Every number returned is a measurement with its evidence attached, and the caller
 * is expected to treat the treatment flag as a *prior* rather than a verdict - the
 * footer text is what finally decides whether a card is borderless, because a
 * borderless printing's footer is its era's footer with the landmarks moved.
 */
/**
 * Label the calibration samples a frame supplies.
 *
 * The positive is the true cut; the negatives are the cuts the detector would make
 * instead - the neighbouring rows two, four and eight percent away. Anything
 * further away is not a mistake the detector can plausibly make, and training
 * against it would just teach the weights to reject impossible answers.
 */
export function windowObservations(profile, truth, options = {}) {
    const offsets = options.offsets ?? [-0.08, -0.04, -0.02, 0.02, 0.04, 0.08];
    const samples = [
        { features: bottomFeatures(profile, truth.y1), positive: true },
        { features: topFeatures(profile, truth.y0), positive: true },
    ];
    for (const offset of offsets) {
        const bottom = truth.y1 + offset;
        if (bottom > 0.05 && bottom < 0.97)
            samples.push({ features: bottomFeatures(profile, bottom), positive: false });
        const top = truth.y0 + offset;
        if (top > 0.01 && top < 0.35)
            samples.push({ features: topFeatures(profile, top), positive: false });
    }
    return samples;
}
export function locateArtWindow(frame, options = {}) {
    const profile = options.profile ?? profileFrame(frame);
    const bottomWeights = options.bottomWeights ?? ART_BOTTOM_WEIGHTS;
    const topWeights = options.topWeights ?? ART_TOP_WEIGHTS;
    // The ranges are the printed window's own limits with room to spare: the picture's
    // bottom edge is at 0.486-0.577 on every card measured, and its top at 0.088-0.147,
    // so a cut that lands on the edge of a search range is at most 1% of the card from
    // the truth. Wider ranges would let the score wander into the type box or the title.
    const [bottomFrom, bottomTo] = options.bottomRange ?? [0.4, 0.7];
    const [topFrom, topTo] = options.topRange ?? [0.07, 0.2];
    const evidence = [];
    const raw = argmax(profile, bottomFrom, bottomTo, bottomWeights, bottomFeatures);
    const bottom = { ...raw, cut: Math.max(bottomFrom, raw.cut - ART_BOTTOM_BIAS) };
    const top = argmax(profile, topFrom, topTo, topWeights, topFeatures);
    const column = columnProfile(frame, Math.max(0.02, top.cut + 0.02), Math.min(0.98, top.cut + 0.3));
    const left = pictureEdge(column, 'left', { searchTo: 0.3 });
    const right = pictureEdge(column, 'right', { searchTo: 0.3 });
    // The sides come from the printed proportions, not from the profile, and that is a
    // finding rather than a shortcut. A printed margin's texture and the quiet parts of
    // a painting are *the same* at the resolution a device hands over: on Invisible
    // Stalker the frame beside the picture measures 3-5 levels of local variance and the
    // picture's own left third measures 3-5 too, because what a flat frame colour and a
    // smooth stretch of sky differ in is colour, not texture. What the profile does
    // contain is the card's printed *border* line - 53 levels of variance at 4.7% of the
    // width on that card - which is the border, not the picture, and a detector that
    // takes it puts the window 2.5% of the card out, worse than the constant.
    //
    // So the sides are the panel's printed 8%, and the only thing the profile decides is
    // whether there *is* a panel: no panel edge and no margin means the picture is the
    // card face, which is what a borderless printing looks like. The truth locator
    // confirms the constant is the right prior - over the real cards measured, the art
    // crop's left edge falls at 0.063-0.118 of the width, centred on 0.077. The honest
    // improvement here is to measure the panel edge in *colour* rather than texture (a
    // column-mean profile rather than a variance one); the ADR names it as the next
    // thing to build, with the error it would have to beat.
    // The one question the profile *can* answer about the sides: is there a margin at
    // all? The outermost 3% of a framed card is border and frame colour - flat - and a
    // borderless printing's outermost 3% is picture, as structured as the picture is.
    // That is what decides between the printed panel's proportions and the bleed a
    // borderless card keeps; the panel's own edge is not measurable here, as above.
    const pictureStd = medianStd(column, 0.3, 0.7);
    const sideMargin = medianStd(column, 0, 0.03);
    const panelFound = sideMargin <= pictureStd * 0.6;
    const inset = panelFound ? PANEL_INSET : BLEED_INSET;
    const x0 = inset;
    const x1 = 1 - inset;
    void left;
    void right;
    // Does the picture continue below the standard bottom? A standard frame puts the
    // type line and then the rules box there; a full-art printing puts more picture.
    const reach = options.fullArtReach ?? 0.2;
    const continued = pictureRowShare(profile, bottom.cut + 0.02, Math.min(0.97, bottom.cut + reach));
    const deep = argmax(profile, Math.min(0.95, bottom.cut + 0.12), 0.95, bottomWeights, bottomFeatures);
    const pictureEnd = pictureBottom(profile, bottom.cut + 0.02, 0.98);
    let y1 = bottom.cut;
    let extended = false;
    // Three witnesses, all required. Rows that still look like a picture report the
    // treatment; a deeper cut the score does not *reject* reports the same thing in the
    // score's own terms; and the deepest row that is still a picture places the edge.
    // The middle one is a margin rather than a win, and that is a correction: a picture
    // that fills the card has nothing below it but more picture, so every cut down there
    // scores alike and no deeper cut can *beat* the standard one - the first version of
    // this required exactly that, and a borderless frame then measured its window at
    // 0.674 of the card where the picture runs to the foot.
    if (continued > 0.8 &&
        deep.cut > bottom.cut + 0.05 &&
        deep.score > bottom.score - FULL_ART_SCORE_MARGIN &&
        pictureEnd !== null &&
        pictureEnd > bottom.cut + 0.05) {
        y1 = pictureEnd;
        extended = true;
        evidence.push(`${(continued * 100).toFixed(0)}% of the rows below ${bottom.cut.toFixed(3)} still look like a picture, ` +
            `and the cut at ${deep.cut.toFixed(3)} scores ${(deep.score - bottom.score).toFixed(3)} against it, ` +
            `so the picture runs on: the deepest row that is still a picture is ${y1.toFixed(3)}`);
    }
    else if (continued > 0.8) {
        evidence.push(`${(continued * 100).toFixed(0)}% of the rows below ${bottom.cut.toFixed(3)} look quiet, but a deeper cut scores ` +
            `${(deep.score - bottom.score).toFixed(3)} against it and the last picture row is ` +
            `${pictureEnd === null ? 'none' : pictureEnd.toFixed(3)}: the printed type box is quiet too, so this is a standard frame`);
    }
    evidence.push(`the picture's top scored ${top.score.toFixed(4)} at ${top.cut.toFixed(3)} (next best ${top.runnerUp.toFixed(4)}) on row variance`);
    evidence.push(`its bottom scored ${bottom.score.toFixed(4)} at ${bottom.cut.toFixed(3)} (next best ${bottom.runnerUp.toFixed(4)}) on the step from picture to printed text`);
    if (panelFound) {
        evidence.push(`the card's outer 3% measures ${sideMargin.toFixed(1)} against the picture's ${pictureStd.toFixed(1)}, so there is a printed margin: ` +
            `the sides use the printed ${inset} / ${(1 - inset).toFixed(2)} proportions`);
    }
    else {
        evidence.push(`the card's outer 3% measures ${sideMargin.toFixed(1)}, as structured as the picture: the picture is the card face, ` +
            `so the sides use the printed ${inset} bleed`);
    }
    const separation = bottom.best - bottom.runnerUp;
    return {
        rect: { x0, y0: top.cut, x1, y1 },
        extended,
        confidence: Math.min(0.97, Math.max(0.35, 0.5 + separation)),
        evidence,
    };
}
//# sourceMappingURL=art-window.js.map